import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { fraudDetectionService } from '../../../services/fraudDetectionService';

const FraudDetectionPanel = ({ transactions, userProfile }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedActivity, setExpandedActivity] = useState(null);

  useEffect(() => {
    if (transactions?.length > 0 && userProfile?.id) {
      analyzeTransactions();
    }
  }, [transactions, userProfile?.id]);

  const analyzeTransactions = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fraudDetectionService?.analyzeTransactionPatterns(transactions, userProfile);
      setAnalysis(result);
    } catch (err) {
      setError(err?.message || 'Failed to analyze transactions');
    } finally {
      setLoading(false);
    }
  };

  const getRiskLevelConfig = (riskLevel) => {
    switch (riskLevel) {
      case 'critical':
        return {
          color: 'text-error',
          bgColor: 'bg-error/10',
          borderColor: 'border-error',
          icon: 'AlertCircle',
          label: 'Critical Risk'
        };
      case 'high':
        return {
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          borderColor: 'border-warning',
          icon: 'AlertTriangle',
          label: 'High Risk'
        };
      case 'medium':
        return {
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          borderColor: 'border-warning',
          icon: 'AlertTriangle',
          label: 'Medium Risk'
        };
      case 'low':
        return {
          color: 'text-success',
          bgColor: 'bg-success/10',
          borderColor: 'border-success',
          icon: 'CheckCircle',
          label: 'Low Risk'
        };
      default:
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted/10',
          borderColor: 'border-border',
          icon: 'Info',
          label: 'Unknown'
        };
    }
  };

  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'critical':
        return { color: 'text-error', bgColor: 'bg-error/10', icon: 'AlertCircle' };
      case 'high':
        return { color: 'text-warning', bgColor: 'bg-warning/10', icon: 'AlertTriangle' };
      case 'medium':
        return { color: 'text-warning', bgColor: 'bg-warning/10', icon: 'AlertTriangle' };
      case 'low':
        return { color: 'text-primary', bgColor: 'bg-primary/10', icon: 'Info' };
      default:
        return { color: 'text-muted-foreground', bgColor: 'bg-muted/10', icon: 'Info' };
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-content py-12 text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground">Analyzing transaction patterns with AI...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="card-content py-8">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
            <p className="text-sm text-error mb-4">{error}</p>
            <Button variant="outline" size="sm" onClick={analyzeTransactions} iconName="RefreshCw">
              Retry Analysis
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return null;
  }

  const riskConfig = getRiskLevelConfig(analysis?.riskLevel);

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="card-title flex items-center gap-2">
              <Icon name="Shield" size={20} className="text-primary" />
              AI Fraud Detection
            </h2>
            <p className="card-description">OpenAI-powered transaction pattern analysis</p>
          </div>
          <Button variant="outline" size="sm" onClick={analyzeTransactions} iconName="RefreshCw">
            Refresh
          </Button>
        </div>
      </div>
      <div className="card-content space-y-6">
        {/* Overall Risk Score */}
        <div className={`p-4 rounded-lg border-2 ${riskConfig?.borderColor} ${riskConfig?.bgColor}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Icon name={riskConfig?.icon} size={24} className={riskConfig?.color} />
              <span className={`text-sm font-semibold ${riskConfig?.color} uppercase`}>
                {riskConfig?.label}
              </span>
            </div>
            <div className="text-right">
              <div className={`text-3xl font-bold ${riskConfig?.color}`}>
                {analysis?.overallRiskScore}
              </div>
              <div className="text-xs text-muted-foreground">Risk Score</div>
            </div>
          </div>
          <div className="w-full bg-muted/30 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${riskConfig?.color?.replace('text-', 'bg-')}`}
              style={{ width: `${analysis?.overallRiskScore}%` }}
            ></div>
          </div>
        </div>

        {/* Suspicious Activities */}
        {analysis?.suspiciousActivities?.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="AlertTriangle" size={16} className="text-warning" />
              Suspicious Activities ({analysis?.suspiciousActivities?.length})
            </h3>
            <div className="space-y-3">
              {analysis?.suspiciousActivities?.map((activity) => {
                const severityConfig = getSeverityConfig(activity?.severity);
                const isExpanded = expandedActivity === activity?.id;
                return (
                  <div
                    key={activity?.id}
                    className={`p-4 rounded-lg border ${severityConfig?.bgColor} cursor-pointer hover:shadow-md transition-all`}
                    onClick={() => setExpandedActivity(isExpanded ? null : activity?.id)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-start gap-2 flex-1">
                        <Icon name={severityConfig?.icon} size={18} className={severityConfig?.color} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-sm font-semibold text-foreground">{activity?.type}</h4>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${severityConfig?.bgColor} ${severityConfig?.color}`}>
                              {activity?.severity}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">{activity?.description}</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-4">
                        <div className={`text-xl font-bold ${severityConfig?.color}`}>
                          {activity?.riskScore}
                        </div>
                        <div className="text-xs text-muted-foreground">Risk</div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-border space-y-2">
                        <div className="flex items-start gap-2">
                          <Icon name="Target" size={14} className="text-primary mt-0.5" />
                          <div className="flex-1">
                            <p className="text-xs font-medium text-foreground mb-1">Recommended Action:</p>
                            <p className="text-xs text-muted-foreground">{activity?.recommendedAction}</p>
                          </div>
                        </div>
                        {activity?.affectedTransactions?.length > 0 && (
                          <div className="flex items-start gap-2">
                            <Icon name="Receipt" size={14} className="text-primary mt-0.5" />
                            <div className="flex-1">
                              <p className="text-xs font-medium text-foreground mb-1">Affected Transactions:</p>
                              <p className="text-xs text-muted-foreground">
                                {activity?.affectedTransactions?.join(', ')}
                              </p>
                            </div>
                          </div>
                        )}
                        {activity?.actionPriority && (
                          <div className="flex items-center gap-2">
                            <Icon name="Flag" size={14} className="text-primary" />
                            <span className="text-xs font-medium text-foreground">Priority:</span>
                            <span className="text-xs text-muted-foreground capitalize">{activity?.actionPriority}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Transaction Patterns */}
        {analysis?.patterns?.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="TrendingUp" size={16} className="text-primary" />
              Detected Patterns
            </h3>
            <div className="space-y-2">
              {analysis?.patterns?.map((pattern, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg ${pattern?.isNormal ? 'bg-success/10' : 'bg-warning/10'}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1">
                      <Icon
                        name={pattern?.isNormal ? 'CheckCircle' : 'AlertTriangle'}
                        size={16}
                        className={pattern?.isNormal ? 'text-success' : 'text-warning'}
                      />
                      <span className="text-sm text-foreground">{pattern?.pattern}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">×{pattern?.frequency}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {analysis?.recommendations?.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Lightbulb" size={16} className="text-primary" />
              AI Recommendations
            </h3>
            <div className="space-y-2">
              {analysis?.recommendations?.map((recommendation, index) => (
                <div key={index} className="flex items-start gap-2 p-3 bg-primary/10 rounded-lg">
                  <Icon name="CheckCircle" size={16} className="text-primary mt-0.5" />
                  <p className="text-sm text-foreground flex-1">{recommendation}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Rail Analysis */}
        {analysis?.paymentRailAnalysis && (
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="CreditCard" size={16} className="text-primary" />
              Payment Rail Analysis
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(analysis?.paymentRailAnalysis)?.map(([rail, data]) => (
                <div key={rail} className="p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon
                      name={rail === 'btc' ? 'Bitcoin' : rail === 'eth' ? 'Hexagon' : rail === 'usdc' ? 'DollarSign' : 'CreditCard'}
                      size={16}
                      className="text-primary"
                    />
                    <span className="text-sm font-semibold text-foreground uppercase">{rail}</span>
                  </div>
                  {data && typeof data === 'object' && Object.keys(data)?.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      {Object.entries(data)?.map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span>{key}:</span>
                          <span className="font-medium">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FraudDetectionPanel;
